<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useChatStore } from '../../stores/chatStore';

const { t } = useI18n();

interface MatterContextEntity {
    entity_slug: string;
    entity_type: string;
    name: string;
    confidence: number;
    confidence_reasoning?: string;
    context: string;
    mentions_across_files: number;
}

interface MatterContextKeyEvent {
    entity_slug: string;
    date?: string;
    confidence: number;
    confidence_reasoning?: string;
    context: string;
    mentions_across_files: number;
}

interface MatterContextRelationship {
    source_entity_slug: string;
    target_entity_slug: string;
    relation_verb: string;
    detail?: string;
}

interface MatterContextDebugData {
    completed_at?: string;
    summary?: string;
    entities?: MatterContextEntity[];
    key_events?: MatterContextKeyEvent[];
    relationships?: MatterContextRelationship[];
}

type SectionKey = 'summary' | 'entities' | 'key_events' | 'relationships';

// Known entity-type keys map to translated headings; anything outside the
// closed taxonomy falls back to the raw type key (data) in headingFor().
const ENTITY_TYPE_HEADING_KEYS: Record<string, string> = {
    individual: 'debug.entityTypePeople',
    organization: 'debug.entityTypeOrganizations',
    group_of_people: 'debug.entityTypeGroups',
    place: 'debug.entityTypePlaces',
    department: 'debug.entityTypeDepartments',
};

const chatStore = useChatStore();
const expandedSections = ref<Record<SectionKey, boolean>>({
    summary: true,
    entities: false,
    key_events: false,
    relationships: false,
});
const copyFeedback = ref(false);

const data = computed(() => chatStore.matterContextDebug as MatterContextDebugData | null);

const toggleSection = (section: SectionKey) => {
    expandedSections.value[section] = !expandedSections.value[section];
};

const isSectionExpanded = (section: SectionKey) => !!expandedSections.value[section];

// Group entities by entity_type. Closed taxonomy:
// individual / organization / group_of_people / place / department.
// Anything outside the closed list falls into an 'other' bucket.
const entitiesByType = computed<Record<string, MatterContextEntity[]>>(() => {
    if (!data.value?.entities) return {};
    const groups: Record<string, MatterContextEntity[]> = {};
    for (const e of data.value.entities) {
        const key = (e.entity_type || 'other').toLowerCase();
        if (!groups[key]) groups[key] = [];
        groups[key].push(e);
    }
    return groups;
});

const headingFor = (typeKey: string) => {
    const key = ENTITY_TYPE_HEADING_KEYS[typeKey];
    return key ? t(key) : typeKey;
};

const copyAsJSON = async () => {
    if (!data.value) return;
    try {
        await navigator.clipboard.writeText(JSON.stringify(data.value, null, 2));
        copyFeedback.value = true;
        setTimeout(() => {
            copyFeedback.value = false;
        }, 2000);
    } catch (err) {
        console.error('Failed to copy:', err);
    }
};
</script>

<template>
    <div v-if="data" class="matter-context-debug-view" style="padding: 16px 24px">
        <div class="mc-debug-header">
            <div class="mc-debug-header-info">
                <span v-if="data.completed_at" class="mc-debug-stat mc-debug-timestamp">
                    {{ new Date(data.completed_at).toLocaleString() }}
                </span>
            </div>
            <button
                class="mc-debug-copy-btn"
                :title="copyFeedback ? t('debug.copied') : t('debug.copyAsJson')"
                @click="copyAsJSON"
            >
                <span class="material-icons" style="font-size: 18px">
                    {{ copyFeedback ? 'check' : 'content_copy' }}
                </span>
            </button>
        </div>

        <div class="mc-debug-section">
            <div class="mc-debug-section-header" @click="toggleSection('summary')">
                <span class="material-icons mc-debug-chevron">
                    {{ isSectionExpanded('summary') ? 'expand_more' : 'chevron_right' }}
                </span>
                <strong>{{ t('debug.summary') }}</strong>
            </div>
            <div v-if="isSectionExpanded('summary')" class="mc-debug-section-body">
                <div v-if="data.summary" class="mc-summary-text" style="white-space: pre-wrap">
                    {{ data.summary }}
                </div>
                <div v-else class="mc-debug-empty">{{ t('debug.noSummary') }}</div>
            </div>
        </div>

        <div class="mc-debug-section">
            <div class="mc-debug-section-header" @click="toggleSection('entities')">
                <span class="material-icons mc-debug-chevron">
                    {{ isSectionExpanded('entities') ? 'expand_more' : 'chevron_right' }}
                </span>
                <strong>{{ t('debug.entitiesHeading', { count: data.entities?.length || 0 }) }}</strong>
            </div>
            <div v-if="isSectionExpanded('entities')" class="mc-debug-section-body">
                <div
                    v-for="(entities, typeKey) in entitiesByType"
                    :key="typeKey"
                    class="mc-entity-group"
                >
                    <div class="mc-entity-group-header">
                        {{ headingFor(typeKey) }} ({{ entities.length }})
                    </div>
                    <div v-for="e in entities" :key="e.entity_slug" class="mc-entity-item">
                        <div class="mc-entity-name">
                            <strong>{{ e.name }}</strong>
                            <span
                                class="mc-entity-confidence"
                                :class="{
                                    'mc-high': e.confidence >= 0.8,
                                    'mc-medium': e.confidence >= 0.5 && e.confidence < 0.8,
                                    'mc-low': e.confidence < 0.5,
                                }"
                            >
                                {{ (e.confidence * 100).toFixed(0) }}%
                            </span>
                        </div>
                        <div class="mc-entity-context">{{ e.context }}</div>
                        <div class="mc-entity-meta">{{ t('debug.mentionCount', e.mentions_across_files) }}</div>
                    </div>
                </div>
                <div v-if="!data.entities?.length" class="mc-debug-empty">
                    {{ t('debug.noEntities') }}
                </div>
            </div>
        </div>

        <div class="mc-debug-section">
            <div class="mc-debug-section-header" @click="toggleSection('key_events')">
                <span class="material-icons mc-debug-chevron">
                    {{ isSectionExpanded('key_events') ? 'expand_more' : 'chevron_right' }}
                </span>
                <strong>{{ t('debug.keyEventsHeading', { count: data.key_events?.length || 0 }) }}</strong>
            </div>
            <div v-if="isSectionExpanded('key_events')" class="mc-debug-section-body">
                <div v-for="ev in data.key_events" :key="ev.entity_slug" class="mc-entity-item">
                    <div class="mc-entity-name">
                        <strong>{{ ev.date || ev.entity_slug }}</strong>
                        <span
                            class="mc-entity-confidence"
                            :class="{
                                'mc-high': ev.confidence >= 0.8,
                                'mc-medium': ev.confidence >= 0.5 && ev.confidence < 0.8,
                                'mc-low': ev.confidence < 0.5,
                            }"
                        >
                            {{ (ev.confidence * 100).toFixed(0) }}%
                        </span>
                    </div>
                    <div class="mc-entity-context">{{ ev.context }}</div>
                </div>
                <div v-if="!data.key_events?.length" class="mc-debug-empty">
                    {{ t('debug.noEvents') }}
                </div>
            </div>
        </div>

        <div class="mc-debug-section">
            <div class="mc-debug-section-header" @click="toggleSection('relationships')">
                <span class="material-icons mc-debug-chevron">
                    {{ isSectionExpanded('relationships') ? 'expand_more' : 'chevron_right' }}
                </span>
                <strong>{{ t('debug.relationshipsHeading', { count: data.relationships?.length || 0 }) }}</strong>
            </div>
            <div v-if="isSectionExpanded('relationships')" class="mc-debug-section-body">
                <div v-for="(r, idx) in data.relationships" :key="idx" class="mc-relationship-item">
                    <div class="mc-relationship-edge">
                        <strong>{{ r.source_entity_slug }}</strong>
                        <span class="mc-relationship-arrow">{{ r.relation_verb }}</span>
                        <strong>{{ r.target_entity_slug }}</strong>
                    </div>
                    <div class="mc-relationship-detail">{{ r.detail }}</div>
                </div>
                <div v-if="!data.relationships?.length" class="mc-debug-empty">
                    {{ t('debug.noRelationships') }}
                </div>
            </div>
        </div>
    </div>
    <div v-else class="mc-debug-empty">{{ t('debug.noMatterContext') }}</div>
</template>